/**
 * The tech tree: 4 tracks × 3 tiers, drawn into a 4-card market.
 *
 * Techs are the game's engine-building axis. Every tech has printed VP so research is
 * never a pure detour, and tiers must be bought in order within a track so a player
 * cannot rush straight to a tier III power.
 */

import type { Player, Tech, TechId, TechTrack } from './types';

export const TECHS: Readonly<Record<TechId, Tech>> = Object.freeze({
  /* ------------------------------------------------------------------- drive */
  driveI: {
    id: 'driveI', track: 'drive', tier: 1, cost: 3, vp: 1,
    name: 'Ion Thrusters',
    blurb: '+1 jump range for all fleets.',
  },
  driveII: {
    id: 'driveII', track: 'drive', tier: 2, cost: 5, vp: 2,
    name: 'Fold Drive',
    blurb: '+1 further jump range. Moving costs 1 less Fuel (min 0).',
  },
  driveIII: {
    id: 'driveIII', track: 'drive', tier: 3, cost: 7, vp: 3,
    name: 'Gate Weaving',
    blurb: '+1 further jump range, and every jump is free.',
  },

  /* ---------------------------------------------------------------- industry */
  industryI: {
    id: 'industryI', track: 'industry', tier: 1, cost: 3, vp: 1,
    name: 'Autofactories',
    blurb: '+1 Ore per round for each Colony you hold.',
  },
  industryII: {
    id: 'industryII', track: 'industry', tier: 2, cost: 5, vp: 2,
    name: 'Hazard Rigs',
    blurb: 'Claiming ignores the hazard surcharge.',
  },
  industryIII: {
    id: 'industryIII', track: 'industry', tier: 3, cost: 7, vp: 3,
    name: 'Orbital Yards',
    blurb: '+1 Command Point each round, and Deploy Fleet costs 1 Ore less.',
  },

  /* --------------------------------------------------------------------- bio */
  bioI: {
    id: 'bioI', track: 'bio', tier: 1, cost: 3, vp: 1,
    name: 'Hydroponics',
    blurb: '+1 Biomass per round.',
  },
  bioII: {
    id: 'bioII', track: 'bio', tier: 2, cost: 5, vp: 2,
    name: 'Gene Tailoring',
    blurb: 'Population upkeep is halved (rounded down).',
  },
  bioIII: {
    id: 'bioIII', track: 'bio', tier: 3, cost: 7, vp: 3,
    name: 'Living Worlds',
    blurb: '+1 Habitability on every system you own.',
  },

  /* ----------------------------------------------------------------- science */
  scienceI: {
    id: 'scienceI', track: 'science', tier: 1, cost: 3, vp: 1,
    name: 'Deep Survey',
    blurb: '+1 Data per round. Survey also reveals a second adjacent system.',
  },
  scienceII: {
    id: 'scienceII', track: 'science', tier: 2, cost: 5, vp: 2,
    name: 'Peer Networks',
    blurb: 'Research costs 2 Data less (min 1).',
  },
  scienceIII: {
    id: 'scienceIII', track: 'science', tier: 3, cost: 7, vp: 3,
    name: 'Grand Theory',
    blurb: '+1 VP for every 2 techs you hold, this one included.',
  },
});

export const ALL_TECH_IDS: readonly TechId[] = Object.keys(TECHS) as TechId[];

/** Tier order within each track, so prerequisites are data rather than a switch. */
export const TRACK_ORDER: Readonly<Record<TechTrack, readonly TechId[]>> = Object.freeze({
  drive: ['driveI', 'driveII', 'driveIII'],
  industry: ['industryI', 'industryII', 'industryIII'],
  bio: ['bioI', 'bioII', 'bioIII'],
  science: ['scienceI', 'scienceII', 'scienceIII'],
});

export function tech(id: TechId): Tech {
  return TECHS[id];
}

export function hasTech(player: Player, id: TechId): boolean {
  return player.techs.includes(id);
}

/** How many tiers deep the player is in a track (0..3). */
export function trackLevel(player: Player, track: TechTrack): number {
  let level = 0;
  for (const id of TRACK_ORDER[track]) {
    if (player.techs.includes(id)) level++;
    else break;
  }
  return level;
}

/**
 * A tech is available only if the tier directly below it in the same track is owned.
 * Tier I always qualifies.
 */
export function prerequisiteMet(player: Player, id: TechId): boolean {
  const t = TECHS[id];
  if (t.tier === 1) return true;
  const order = TRACK_ORDER[t.track];
  const previous = order[t.tier - 2]!;
  return player.techs.includes(previous);
}

/** Data cost after the player's own discounts. Never below 1. */
export function researchCost(player: Player, id: TechId): number {
  const base = TECHS[id].cost;
  const discount = hasTech(player, 'scienceII') ? 2 : 0;
  return Math.max(1, base - discount);
}

/* ----------------------------------------------------- derived player modifiers */

/** Extra hexes a fleet may travel per Move action. */
export function jumpRangeBonus(player: Player): number {
  return trackLevel(player, 'drive') + (player.megastructure === 'gateNexus' ? 2 : 0);
}

/** Fuel discount per Move action, from Fold Drive. */
export function moveFuelDiscount(player: Player): number {
  if (hasTech(player, 'driveIII')) return Infinity; // Gate Weaving: all jumps free.
  return hasTech(player, 'driveII') ? 1 : 0;
}

/** Extra Command Points from tech and megastructures. */
export function cpBonus(player: Player): number {
  let bonus = 0;
  if (hasTech(player, 'industryIII')) bonus += 1;
  if (player.megastructure === 'gateNexus') bonus += 1;
  return bonus;
}

/** Habitability bonus applied to every system the player owns. */
export function habitabilityBonus(player: Player): number {
  return hasTech(player, 'bioIII') ? 1 : 0;
}

/** Whether the player ignores the hazard surcharge when claiming. */
export function ignoresHazardCost(player: Player): boolean {
  return hasTech(player, 'industryII');
}

/** Ore discount when deploying a fleet. */
export function deployOreDiscount(player: Player): number {
  if (player.megastructure === 'arkFoundry') return Infinity; // free fleets
  return hasTech(player, 'industryIII') ? 1 : 0;
}
